import { createContext, useContext, useEffect, useMemo, useState } from 'react'

const TelegramContext = createContext({})

const parseAdminIds = () => {
  const raw = import.meta.env.VITE_ADMIN_IDS || ''
  return raw
    .split(',')
    .map(id => parseInt(id.trim(), 10))
    .filter(Number.isFinite)
}

export const TelegramProvider = ({ children }) => {
  const [tg] = useState(window.Telegram?.WebApp)
  const [user, setUser] = useState(null)
  const [isAdmin, setIsAdmin] = useState(null)
  const adminIds = useMemo(parseAdminIds, [])

  useEffect(() => {
    if (tg) {
      tg.ready()
      tg.expand()
      
      // Получаем данные пользователя
      const telegramUser = tg.initDataUnsafe?.user
      if (telegramUser) {
        setUser(telegramUser)
      } else {
        // РЕЖИМ РАЗРАБОТКИ: если нет пользователя Telegram - создаем тестового
        console.warn('⚠️ Telegram user not found, using dev mode')
        setUser({ id: 1179197893, first_name: 'Dev User' })
      }
    } else {
      // Если вообще нет Telegram WebApp - используем тестового пользователя
      console.warn('⚠️ Telegram WebApp not found, using dev mode')
      setUser({ id: 1179197893, first_name: 'Dev User' })
    }
  }, [tg])

  useEffect(() => {
    const checkAdminStatus = async () => {
      console.log('🔍 Начинаем проверку прав администратора...')
      console.log('  - User:', user)
      console.log('  - Admin IDs from env:', adminIds)
      
      // Если есть локальные админ ID из .env
      if (adminIds.length > 0 && user?.id) {
        const isAdminLocal = adminIds.includes(Number(user.id))
        console.log(`✅ Проверка через .env: ${isAdminLocal}`)
        setIsAdmin(isAdminLocal)
        return
      }

      // Иначе проверяем через API
      if (user?.id) {
        try {
          const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'
          console.log(`📡 Проверяем через API: ${API_URL}/api/check-admin/${user.id}`)
          
          const response = await fetch(`${API_URL}/api/check-admin/${user.id}`)
          const data = await response.json()
          
          console.log('📥 Ответ API:', data)
          setIsAdmin(data.is_admin === true)
          
          // Показываем в консоли для отладки
          if (data.demo_mode) {
            console.log('🔧 DEMO MODE: Все пользователи являются администраторами')
          }
          console.log(`✅ Результат: User ID: ${user.id}, Admin: ${data.is_admin}`)
        } catch (error) {
          console.error('❌ Ошибка проверки прав администратора:', error)
          // В режиме разработки делаем всех админами
          if (import.meta.env.DEV) {
            console.log('🔧 DEV MODE: Права администратора включены для разработки')
            setIsAdmin(true)
          } else {
            console.log('⚠️ Права администратора отключены из-за ошибки')
            setIsAdmin(false)
          }
        }
      } else {
        console.log('⚠️ Пользователь не определен')
        setIsAdmin(null)
      }
    }

    checkAdminStatus()
  }, [user, adminIds])

  const showMainButton = (text, onClick) => {
    if (tg?.MainButton) {
      tg.MainButton.setText(text)
      tg.MainButton.onClick(onClick)
      tg.MainButton.show()
    }
  }

  const hideMainButton = () => {
    if (tg?.MainButton) {
      tg.MainButton.hide()
    }
  }

  const showBackButton = (onClick) => {
    if (tg?.BackButton) {
      tg.BackButton.onClick(onClick)
      tg.BackButton.show()
    }
  }

  const hideBackButton = () => {
    if (tg?.BackButton) {
      tg.BackButton.hide()
    }
  }

  const showAlert = (message) => {
    if (tg) {
      tg.showAlert(message)
    } else {
      alert(message)
    }
  }

  const showConfirm = (message) => {
    if (tg) {
      return tg.showConfirm(message)
    } else {
      return Promise.resolve(confirm(message))
    }
  }

  const hapticFeedback = (type = 'medium') => {
    if (tg?.HapticFeedback) {
      if (type === 'light') {
        tg.HapticFeedback.impactOccurred('light')
      } else if (type === 'medium') {
        tg.HapticFeedback.impactOccurred('medium')
      } else if (type === 'heavy') {
        tg.HapticFeedback.impactOccurred('heavy')
      } else if (type === 'success') {
        tg.HapticFeedback.notificationOccurred('success')
      } else if (type === 'error') {
        tg.HapticFeedback.notificationOccurred('error')
      } else if (type === 'warning') {
        tg.HapticFeedback.notificationOccurred('warning')
      }
    }
  }

  const close = () => {
    if (tg) {
      tg.close()
    }
  }

  const value = {
    tg,
    user,
    showMainButton,
    hideMainButton,
    showBackButton,
    hideBackButton,
    isAdmin,
    showAlert,
    showConfirm,
    hapticFeedback,
    close
  }

  return (
    <TelegramContext.Provider value={value}>
      {children}
    </TelegramContext.Provider>
  )
}

export const useTelegram = () => useContext(TelegramContext)